import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { formatIndianCurrency } from '../../utils/formatters';
import { 
  Users, 
  Shield, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  CreditCard,
  DollarSign,  // 👈 Added for earnings
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    kycPending: 0,
    kycApproved: 0,
    totalDeposits: 0,
    totalInvested: 0,
    pendingWithdrawals: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/system/stats');
      setStats(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Platform overview and key metrics</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">Total Users</p>
              <div className="p-3 bg-primary-light rounded-lg">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
            <p className="text-sm text-gray-600 mt-2">Registered members</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">KYC Pending</p>
              <div className="p-3 bg-warning-light rounded-lg">
                <Clock className="w-6 h-6 text-warning" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.kycPending}</p>
            <p className="text-sm text-gray-600 mt-2">Awaiting review</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">Total Invested</p>
              <div className="p-3 bg-success-light rounded-lg">
                <TrendingUp className="w-6 h-6 text-success" />
              </div>
            </div>
            <p className="text-2xl font-mono font-bold text-gray-900">
              {formatIndianCurrency(stats.totalInvested || 0)}
            </p>
            <p className="text-sm text-gray-600 mt-2">{stats.totalDeposits || 0} deposits</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-600 uppercase">Withdrawals</p>
              <div className="p-3 bg-error-light rounded-lg">
                <Shield className="w-6 h-6 text-error" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.pendingWithdrawals}</p>
            <p className="text-sm text-gray-600 mt-2">Pending approval</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <a
            href="/admin/users"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-primary hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-8 h-8 text-primary" />
              <h3 className="font-semibold text-gray-900">Users Management</h3>
            </div>
            <p className="text-sm text-gray-600">Search, create, and manage users</p>
          </a>

          <a
            href="/admin/deposits"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-success hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-8 h-8 text-success" />
              <h3 className="font-semibold text-gray-900">Deposits</h3>
            </div>
            <p className="text-sm text-gray-600">Approve deposit requests</p>
          </a>
          
          <a
            href="/admin/kyc"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-warning hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-8 h-8 text-warning" />
              <h3 className="font-semibold text-gray-900">KYC Review</h3>
            </div>
            <p className="text-sm text-gray-600">Review and approve KYC submissions</p>
          </a>

          {/* 👇 NEW: Daily Sales Report Card */}
          <a
            href="/admin/earnings"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-emerald-500 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="w-8 h-8 text-emerald-600" />
              <h3 className="font-semibold text-gray-900">Daily Sales Report</h3>
            </div>
            <p className="text-sm text-gray-600">View platform-wide earnings & statistics</p>
          </a>

          <a
            href="/admin/performance"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-primary hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-8 h-8 text-primary" />
              <h3 className="font-semibold text-gray-900">Performance Periods</h3>
            </div>
            <p className="text-sm text-gray-600">Manage profit posting and commissions</p>
          </a>

          <a
            href="/admin/withdrawals"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-success hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-8 h-8 text-success" />
              <h3 className="font-semibold text-gray-900">Withdrawals</h3>
            </div>
            <p className="text-sm text-gray-600">Approve and process withdrawal requests</p>
          </a>

          <a
            href="/admin/mlm-settings"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-warning hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-8 h-8 text-warning" />
              <h3 className="font-semibold text-gray-900">MLM Settings</h3>
            </div>
            <p className="text-sm text-gray-600">Configure commission rates and rewards</p>
          </a>

          <a
            href="/admin/bank-details"
            className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-primary hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <CreditCard className="w-8 h-8 text-primary" />
              <h3 className="font-semibold text-gray-900">Bank Accounts</h3>
            </div>
            <p className="text-sm text-gray-600">Manage payout bank / UPI beneficiaries</p>
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;